import { effect, inject, Injectable, InjectionToken, signal, untracked } from '@angular/core';
import { NavigationError } from '@angular/router';
import { ConnectivityService } from './connectivity.service';

/**
 * Reloads the current document. Declared as a token so the reload can be replaced in tests.
 */
export const RELOAD_PAGE = new InjectionToken<() => void>('Reloads the current document', {
  providedIn: 'root',
  factory: () => () => window.location.reload(),
});

/**
 * Messages used by the browsers when a `import()` cannot be fetched. The browser caches the failure
 * in its module map, so the route stays broken until the document is reloaded.
 */
const DYNAMIC_IMPORT_FAILURE_PATTERNS = [
  'failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // WebKit
  'failed to fetch', // generic fetch failure while fetching the chunk
];

/** Whether the error looks like a lazy chunk that could not be fetched. */
export function isDynamicImportFailure(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');

  return DYNAMIC_IMPORT_FAILURE_PATTERNS.some((pattern) => message.toLowerCase().includes(pattern));
}

/**
 * @description
 * Recovery for a navigation that failed because a lazy chunk could not be loaded while offline.
 *
 * The browser caches the failed module fetch, so navigating to that route keeps failing until the
 * document is reloaded. Reloading does not fix it either while there is no connection (the chunk
 * still cannot be fetched), so the reload is deferred until the connection is back.
 *
 * Meant to be registered with `withNavigationErrorHandler`.
 */
@Injectable({
  providedIn: 'root',
})
export class OfflineNavigationRecoveryService {
  private readonly connectivity = inject(ConnectivityService);
  private readonly reloadPage = inject(RELOAD_PAGE);

  private readonly _isReloadPending = signal(false);

  constructor() {
    effect(() => {
      if (this._isReloadPending() && this.connectivity.isOnline()) {
        untracked(() => this.reloadPage());
      }
    });
  }

  /** Navigation error handler: marks the app to be reloaded when a chunk failed while offline. */
  public handleNavigationError(navigationError: NavigationError): void {
    const { error } = navigationError;

    if (this.connectivity.isOffline() && isDynamicImportFailure(error)) {
      this._isReloadPending.set(true);
      return;
    }

    // The router no longer rethrows the navigation errors to the global error handler when a
    // navigation error handler is provided, so the ones we do not recover from are logged here.
    console.error('Navigation error:', navigationError);
  }
}
